import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaInstagram, FaFacebook, FaYoutube } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import "./HeroSection.css";
import logo from "../../../Assets/Logos/sLogo.png";
import roundedImg from "../../../Assets/Images/common/rounded.png";

const NewHeroSection = () => {
  const [heroContent, setHeroContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactData, setContactData] = useState(null);

  // Carousel slide index
  const [currentSlide, setCurrentSlide] = useState(0);

  const fetchData = async () => {
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/hero-contents/get-hero`
      );
      setHeroContent(resp.data?.hero);
    } catch (error) {
      console.error("Error fetching Hero Content", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, offset: 100 });
    fetchData();
  }, []);

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/contactinfo/get-contact`
        );
        setContactData(response.data.data);
      } catch (error) {
        console.error("Error fetching contact data:", error);
      }
    };

    fetchContactData();
  }, []);

  // Extract media values
  const mediaType = heroContent?.media?.type;
  const videoUrl = heroContent?.media?.videoUrl;
  const images = heroContent?.media?.images || [];

  // Auto-slide for banners
  useEffect(() => {
    if (mediaType !== "image" || images.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 3000); // change slide every 3 sec

    return () => clearInterval(interval);
  }, [mediaType, images]);

  return (
    <>
      {/* -------------- INLINE CSS FOR ROTATING IMAGE -------------- */}
      <style>
        {`
          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          .rotating-image-container {
            animation: rotate 10s linear infinite;
            transform-origin: center;
          }
        `}
      </style>

      <section className="relative md:h-[80vh] h-[60vh] w-full flex md:items-center items-start px-4 md:px-8 lg:px-12 overflow-hidden pt-9 sm:pt-0">

        {/* -------------- BACKGROUND VIDEO -------------- */}
        {mediaType === "video" && (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={videoUrl}
            autoPlay
            loop
            muted
            playsInline
          />
        )}

        {/* -------------- BACKGROUND IMAGE CAROUSEL -------------- */}
        {mediaType === "image" && images.length > 0 && (
          <div className="absolute inset-0 w-full h-full overflow-hidden">
            <div className="w-full h-full relative">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Slide ${index}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${
                    index === currentSlide ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>

        {/* -------------- TEXT CONTENT -------------- */}
        <div
          className="relative z-10 w-full max-w-4xl text-white"
          data-aos="fade-up"
        >
          <p
            className="text-[#FFCCCC] uppercase tracking-wider font-subheading text-sm font-semibold mb-2"
            data-aos="fade-down"
          >
            * Strikes & Shots
          </p>

          {loading ? (
            <div>
              <div className="w-[80%] h-10 md:h-14 bg-gray-600/50 rounded-md animate-pulse mb-6"></div>
              <div className="w-full h-10 md:h-14 bg-gray-600/50 rounded-md animate-pulse mb-6"></div>
            </div>
          ) : (
            <h1
              className="font-heading text-2xl md:text-5xl lg:text-6xl font-semibold !leading-snug mb-6"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              {heroContent?.heading}
            </h1>
          )}

          {loading ? (
            <div className="space-y-2">
              <div className="w-full h-4 bg-gray-600/50 rounded-md animate-pulse"></div>
              <div className="w-5/6 h-4 bg-gray-600/50 rounded-md animate-pulse"></div>
            </div>
          ) : (
            <p
              className="font-subheading tracking-wider text-base md:text-lg text-gray-200"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              {heroContent?.subHeading}
            </p>
          )}
        </div>

        {/* -------------- SOCIAL ICONS -------------- */}
        <div
          className="absolute right-4 md:right-8 lg:right-12 top-10 flex flex-col gap-6 text-2xl z-10"
          data-aos="fade-left"
          data-aos-delay="600"
        >
          {contactData?.socialLinks?.instagram && (
            <a href={contactData.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
              <FaInstagram className="text-white hover:text-primaryBtnHoverColor transition duration-150" />
            </a>
          )}

          {contactData?.socialLinks?.facebook && (
            <a href={contactData.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
              <FaFacebook className="text-white hover:text-primaryBtnHoverColor transition duration-150" />
            </a>
          )}

          {contactData?.socialLinks?.youtube && (
            <a href={contactData.socialLinks.youtube} target="_blank" rel="noopener noreferrer">
              <FaYoutube className="text-white hover:text-primaryBtnHoverColor transition duration-150" />
            </a>
          )}
        </div>

        {/* -------------- ROTATING CIRCLE LOGO -------------- */}
        <div className="absolute bottom-8 right-8 z-10 flex items-center justify-center hidden sm:block">
          <div className="relative w-32 h-32">
            {/* Rotating rounded image */}
            <div className="absolute inset-0 flex items-center justify-center rotating-image-container">
              <img 
                src={roundedImg} 
                alt="Rounded" 
                className="w-full h-full object-contain"
              />
            </div>

            {/* Center logo */}
            <div className="absolute inset-0 flex items-center justify-center">
              <img src={logo} alt="Logo" className="h-12 w-12" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NewHeroSection;





// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { FaInstagram, FaFacebook, FaYoutube } from "react-icons/fa";
// import AOS from "aos";
// import "aos/dist/aos.css";
// import "./HeroSection.css";
// import logo from "../../../Assets/Logos/sLogo.png";

// const NewHeroSection = () => {
//   const [heroContent, setHeroContent] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [contactData, setContactData] = useState(null);

//   // Carousel slide index
//   const [currentSlide, setCurrentSlide] = useState(0);

//   const fetchData = async () => {
//     try {
//       const resp = await axios.get(
//         `${process.env.REACT_APP_API_URL}/hero-contents/get-hero`
//       );
//       setHeroContent(resp.data?.hero);
//     } catch (error) {
//       console.error("Error fetching Hero Content", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     AOS.init({ duration: 1000, once: true, offset: 100 });
//     fetchData();
//   }, []);

//   useEffect(() => {
//     const fetchContactData = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_API_URL}/contactinfo/get-contact`
//         );
//         setContactData(response.data.data);
//       } catch (error) {
//         console.error("Error fetching contact data:", error);
//       }
//     };

//     fetchContactData();
//   }, []);

//   // Extract media values
//   const mediaType = heroContent?.media?.type;
//   const videoUrl = heroContent?.media?.videoUrl;
//   const images = heroContent?.media?.images || [];

//   // Auto-slide for banners
//   useEffect(() => {
//     if (mediaType !== "image" || images.length === 0) return;

//     const interval = setInterval(() => {
//       setCurrentSlide((prev) => (prev + 1) % images.length);
//     }, 3000); // change slide every 3 sec

//     return () => clearInterval(interval);
//   }, [mediaType, images]);

//   return (
//     <section className="relative md:h-[80vh] h-[60vh] w-full flex md:items-center items-start px-4 md:px-8 lg:px-12 overflow-hidden pt-9 sm:pt-0">

//       {/* -------------- BACKGROUND VIDEO -------------- */}
//       {mediaType === "video" && (
//         <video
//           className="absolute inset-0 w-full h-full object-cover"
//           src={videoUrl}
//           autoPlay
//           loop
//           muted
//           playsInline
//         />
//       )}

//       {/* -------------- BACKGROUND IMAGE CAROUSEL -------------- */}
//       {mediaType === "image" && images.length > 0 && (
//         <div className="absolute inset-0 w-full h-full overflow-hidden">
//           <div className="w-full h-full relative">
//             {images.map((img, index) => (
//               <img
//                 key={index}
//                 src={img}
//                 alt={`Slide ${index}`}
//                 className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${
//                   index === currentSlide ? "opacity-100" : "opacity-0"
//                 }`}
//               />
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Overlay */}
//       <div className="absolute inset-0 bg-black bg-opacity-60"></div>

//       {/* -------------- TEXT CONTENT -------------- */}
//       <div
//         className="relative z-10 w-full max-w-4xl text-white"
//         data-aos="fade-up"
//       >
//         <p
//           className="text-[#FFCCCC] uppercase tracking-wider font-subheading text-sm font-semibold mb-2"
//           data-aos="fade-down"
//         >
//           * Strikes & Shots
//         </p>

//         {loading ? (
//           <div>
//             <div className="w-[80%] h-10 md:h-14 bg-gray-600/50 rounded-md animate-pulse mb-6"></div>
//             <div className="w-full h-10 md:h-14 bg-gray-600/50 rounded-md animate-pulse mb-6"></div>
//           </div>
//         ) : (
//           <h1
//             className="font-heading text-2xl md:text-5xl lg:text-6xl font-semibold !leading-snug mb-6"
//             data-aos="zoom-in"
//             data-aos-delay="200"
//           >
//             {heroContent?.heading}
//           </h1>
//         )}

//         {loading ? (
//           <div className="space-y-2">
//             <div className="w-full h-4 bg-gray-600/50 rounded-md animate-pulse"></div>
//             <div className="w-5/6 h-4 bg-gray-600/50 rounded-md animate-pulse"></div>
//           </div>
//         ) : (
//           <p
//             className="font-subheading tracking-wider text-base md:text-lg text-gray-200"
//             data-aos="fade-up"
//             data-aos-delay="400"
//           >
//             {heroContent?.subHeading}
//           </p>
//         )}
//       </div>

//       {/* -------------- SOCIAL ICONS -------------- */}
//       <div
//         className="absolute right-4 md:right-8 lg:right-12 top-10 flex flex-col gap-6 text-2xl z-10"
//         data-aos="fade-left"
//         data-aos-delay="600"
//       >
//         {contactData?.socialLinks?.instagram && (
//           <a href={contactData.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
//             <FaInstagram className="text-white hover:text-primaryBtnHoverColor transition duration-150" />
//           </a>
//         )}

//         {contactData?.socialLinks?.facebook && (
//           <a href={contactData.socialLinks.facebook} target="_blank" rel="noopener noreferrer">
//             <FaFacebook className="text-white hover:text-primaryBtnHoverColor transition duration-150" />
//           </a>
//         )}

//         {contactData?.socialLinks?.youtube && (
//           <a href={contactData.socialLinks.youtube} target="_blank" rel="noopener noreferrer">
//             <FaYoutube className="text-white hover:text-primaryBtnHoverColor transition duration-150" />
//           </a>
//         )}
//       </div>

//       {/* -------------- ROTATING CIRCLE LOGO -------------- */}
//       {/* <div className="absolute bottom-8 right-8 z-10 flex items-center justify-center hidden sm:block">
//         <div className="relative w-32 h-32">
//           <div className="absolute inset-0 flex items-center justify-center">
//             <svg viewBox="0 0 100 100" className="w-full h-full hero-arrow-svg">
//               <defs>
//                 <path
//                   id="circlePath"
//                   d="
//                     M 50, 50
//                     m -35, 0
//                     a 35,35 0 1,1 70,0
//                     a 35,35 0 1,1 -70,0
//                   "
//                 />
//               </defs>
//               <text fill="white" fontSize="6" fontWeight="bold">
//                 <textPath
//                   href="#circlePath"
//                   startOffset="0%"
//                   className="rotating-text-path tracking-widest font-heading"
//                 >
//                   STRIKES & SHOTS • STRIKES & SHOTS •
//                 </textPath>
//               </text>
//             </svg>
//           </div>

//           <div className="absolute inset-0 flex items-center justify-center">
//             <img src={logo} alt="Logo" className="h-12 w-12" />
//           </div>
//         </div>
//       </div> */}
//     </section>
//   );
// };

// export default NewHeroSection;